import { useState, useEffect, useRef } from 'react';
import { XMarkIcon, PlusIcon, CodeBracketIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import type { Flow, Etapa } from '../../types';

interface EditorDocumentoProps {
  flow: Flow;
  template?: any; // Template existente para editar, ou undefined para criar novo
  onSave: (template: any) => void;
  onClose: () => void;
}

const EditorDocumento = ({ flow, template, onSave, onClose }: EditorDocumentoProps) => {
  const [nome, setNome] = useState(template?.nome || '');
  const [descricao, setDescricao] = useState(template?.descricao || '');
  const [etapaSelecionada, setEtapaSelecionada] = useState<string>(template?.etapaId || '');
  const [conteudoHtml, setConteudoHtml] = useState(template?.conteudoHtml || '<p>Digite seu documento aqui...</p>');
  const [ativo, setAtivo] = useState(template?.ativo !== false);
  const [mostrarCampos, setMostrarCampos] = useState(false);
  const [modoVisual, setModoVisual] = useState(true); // Sempre visual por padrão
  const [tipoDocumento, setTipoDocumento] = useState<'html' | 'pdf'>(template?.tipo || 'html');
  const [pdfUrl, setPdfUrl] = useState(template?.pdfUrl || '');
  const [pdfNomeOriginal, setPdfNomeOriginal] = useState(template?.pdfNomeOriginal || '');
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);

  // Inicializar conteúdo do editor
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = conteudoHtml;
    }
  }, []);

  // Obter todos os campos disponíveis do fluxo e etapas
  const camposDisponiveis = (): Array<{ label: string; tag: string; categoria: string }> => {
    const campos: Array<{ label: string; tag: string; categoria: string }> = [];

    // Campos do fluxo
    campos.push({ label: 'Nome do Fluxo', tag: '{{fluxo.nome}}', categoria: 'Fluxo' });
    campos.push({ label: 'Data Atual', tag: '{{data.atual}}', categoria: 'Sistema' });
    campos.push({ label: 'Hora Atual', tag: '{{hora.atual}}', categoria: 'Sistema' });

    // Campos do card
    campos.push({ label: 'ID do Card', tag: '{{card.id}}', categoria: 'Card' });
    campos.push({ label: 'Título do Card', tag: '{{card.titulo}}', categoria: 'Card' });
    campos.push({ label: 'Etapa Atual', tag: '{{card.etapa}}', categoria: 'Card' });
    campos.push({ label: 'Data de Criação', tag: '{{card.dataCriacao}}', categoria: 'Card' });

    // Campos dinâmicos das etapas
    const etapa = etapaSelecionada 
      ? flow.etapas.find(e => e.id === etapaSelecionada)
      : null;

    if (etapa) {
      etapa.campos.forEach(campo => {
        campos.push({
          label: campo.nome,
          tag: `{{${campo.nome}}}`,
          categoria: `Campos de ${etapa.nome}`
        });
      });
    } else {
      // Se não tem etapa selecionada, mostrar campos de todas as etapas
      flow.etapas.forEach(etapa => {
        etapa.campos.forEach(campo => {
          campos.push({
            label: `${campo.nome} (${etapa.nome})`,
            tag: `{{${campo.nome}}}`,
            categoria: 'Todos os Campos'
          });
        });
      });
    }

    return campos;
  };

  const inserirCampo = (tag: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertHTML', false, tag);
      setConteudoHtml(editorRef.current.innerHTML);
    }
  };

  const aplicarFormatacao = (comando: string, valor?: string) => {
    document.execCommand(comando, false, valor);
    if (editorRef.current) {
      setConteudoHtml(editorRef.current.innerHTML);
    }
  };

  const inserirImagem = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const img = `<img src="${event.target?.result}" style="max-width: 100%; height: auto; margin: 10px 0;" />`;
          document.execCommand('insertHTML', false, img);
          if (editorRef.current) {
            setConteudoHtml(editorRef.current.innerHTML);
          }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const inserirLink = () => {
    const url = prompt('Digite a URL do link:');
    if (url) {
      const texto = prompt('Digite o texto do link:') || url;
      const link = `<a href="${url}" target="_blank" style="color: #2563eb; text-decoration: underline;">${texto}</a>`;
      document.execCommand('insertHTML', false, link);
      if (editorRef.current) {
        setConteudoHtml(editorRef.current.innerHTML);
      }
    }
  };

  const inserirTabela = () => {
    const linhas = parseInt(prompt('Quantas linhas?') || '3');
    const colunas = parseInt(prompt('Quantas colunas?') || '3');
    
    let tabela = '<table style="border-collapse: collapse; width: 100%; margin: 10px 0;">';
    for (let i = 0; i < linhas; i++) {
      tabela += '<tr>';
      for (let j = 0; j < colunas; j++) {
        tabela += '<td style="border: 1px solid #ddd; padding: 8px;">&nbsp;</td>';
      }
      tabela += '</tr>';
    }
    tabela += '</table>';
    
    document.execCommand('insertHTML', false, tabela);
    if (editorRef.current) {
      setConteudoHtml(editorRef.current.innerHTML);
    }
  };

  const inserirLogo = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const logo = `<div style="text-align: center; margin: 20px 0;"><img src="${event.target?.result}" style="max-width: 200px; height: auto;" /></div>`;
          document.execCommand('insertHTML', false, logo);
          if (editorRef.current) {
            setConteudoHtml(editorRef.current.innerHTML);
          }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleUploadPdf = async (file: File) => {
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/documentos/templates/upload-pdf', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setPdfUrl(data.pdfUrl);
        setPdfNomeOriginal(data.nomeOriginal);
      } else {
        const error = await response.json();
        alert(`Erro ao fazer upload: ${error.error || 'Erro desconhecido'}`);
      }
    } catch (error) {
      console.error('Erro ao fazer upload do PDF:', error);
      alert('Erro ao fazer upload do PDF');
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleSalvar = () => {
    if (!nome.trim()) {
      alert('Digite um nome para o documento');
      return;
    }

    if (tipoDocumento === 'html' && !conteudoHtml.trim()) {
      alert('O conteúdo HTML não pode estar vazio');
      return;
    }

    if (tipoDocumento === 'pdf' && !pdfUrl) {
      alert('Faça o upload de um arquivo PDF');
      return;
    }

    // Determinar se é um template do MongoDB (tem _id de 24 caracteres) ou novo (id numérico)
    const isMongoTemplate = template?._id && typeof template._id === 'string' && template._id.length === 24;

    const novoTemplate = {
      ...(isMongoTemplate && { _id: template._id }), // Preservar _id apenas se for do MongoDB
      nome: nome.trim(),
      descricao: descricao.trim(),
      flowId: flow.id,
      flowNome: flow.name,
      etapaId: etapaSelecionada || undefined,
      etapaNome: etapaSelecionada 
        ? flow.etapas.find(e => e.id === etapaSelecionada)?.nome 
        : undefined,
      tipo: tipoDocumento,
      conteudoHtml: tipoDocumento === 'html' ? conteudoHtml.trim() : '',
      pdfUrl: tipoDocumento === 'pdf' ? pdfUrl : '',
      pdfNomeOriginal: tipoDocumento === 'pdf' ? pdfNomeOriginal : '',
      ativo,
      dataAtualizacao: new Date().toISOString()
    };

    console.log('💾 Salvando template:', novoTemplate);
    console.log('🔍 É template do MongoDB?', isMongoTemplate);
    onSave(novoTemplate);
  };

  const templatePadrao = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <style>
        body { 
            font-family: Arial, sans-serif; 
            margin: 40px;
            line-height: 1.6;
        }
        .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
        }
        .content {
            margin: 20px 0;
        }
        .footer {
            margin-top: 50px;
            text-align: center;
            font-size: 12px;
            color: #666;
        }
    </style>
</head>
<body>
    <div class="header">
        <h1>{{fluxo.nome}}</h1>
        <p>Data: {{data.atual}}</p>
    </div>
    
    <div class="content">
        <h2>{{card.titulo}}</h2>
        <p><strong>Etapa:</strong> {{card.etapa}}</p>
        
        <!-- Adicione mais campos aqui -->
        
    </div>
    
    <div class="footer">
        <p>Documento gerado automaticamente em {{data.atual}} às {{hora.atual}}</p>
    </div>
</body>
</html>
  `.trim();

  const agruparCamposPorCategoria = () => {
    const campos = camposDisponiveis();
    const grupos: Record<string, typeof campos> = {};
    
    campos.forEach(campo => {
      if (!grupos[campo.categoria]) {
        grupos[campo.categoria] = [];
      }
      grupos[campo.categoria].push(campo);
    });
    
    return grupos;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {template ? 'Editar Documento' : 'Novo Documento'}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {tipoDocumento === 'html' 
                ? 'Crie documentos timbrados com campos dinâmicos'
                : 'Importe um PDF pronto como documento padrão'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Painel Esquerdo - Configurações */}
          <div className="w-80 border-r border-gray-200 p-6 overflow-y-auto bg-gray-50">
            <div className="space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Documento *
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  placeholder="Ex: Contrato de Prestação de Serviços"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={descricao}
                  onChange={(e) => setDescricao(e.target.value)}
                  placeholder="Breve descrição do documento"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>

              {/* Tipo de Documento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Documento
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setTipoDocumento('html')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                      tipoDocumento === 'html'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    ✏️ Editor com Campos
                  </button>
                  <button
                    onClick={() => setTipoDocumento('pdf')}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                      tipoDocumento === 'pdf'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    📄 Importar PDF
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {tipoDocumento === 'html'
                    ? 'Crie um documento com campos dinâmicos preenchidos automaticamente'
                    : 'Importe um PDF pronto para usar como documento padrão'}
                </p>
              </div>

              {/* Etapa */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vincular à Etapa (opcional)
                </label>
                <select
                  value={etapaSelecionada}
                  onChange={(e) => setEtapaSelecionada(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Todas as etapas</option>
                  {flow.etapas.map(etapa => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.nome}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Vincule a uma etapa específica para filtrar os campos disponíveis
                </p>
              </div>

              {/* Status */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="ativo"
                  checked={ativo}
                  onChange={(e) => setAtivo(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="ativo" className="text-sm font-medium text-gray-700 cursor-pointer">
                  Documento ativo
                </label>
              </div>

              {/* Template Padrão */}
              {tipoDocumento === 'html' && !template && conteudoHtml === '' && (
                <button
                  onClick={() => setConteudoHtml(templatePadrao)}
                  className="w-full px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors text-sm font-medium"
                >
                  Usar Template Padrão
                </button>
              )}

              {/* Campos Dinâmicos - só para tipo HTML */}
              {tipoDocumento === 'html' && (
              <div className="border-t border-gray-300 pt-4">
                <button
                  onClick={() => setMostrarCampos(!mostrarCampos)}
                  className="w-full flex items-center justify-between px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium"
                >
                  <span className="flex items-center gap-2">
                    <CodeBracketIcon className="w-5 h-5" />
                    Campos Dinâmicos
                  </span>
                  <span>{mostrarCampos ? '▼' : '▶'}</span>
                </button>

                {mostrarCampos && (
                  <div className="mt-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                    {Object.entries(agruparCamposPorCategoria()).map(([categoria, campos]) => (
                      <div key={categoria} className="border-b border-gray-200 last:border-b-0">
                        <div className="bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700">
                          {categoria}
                        </div>
                        <div className="p-2 space-y-1">
                          {campos.map((campo, idx) => (
                            <button
                              key={idx}
                              onClick={() => inserirCampo(campo.tag)}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 rounded text-sm transition-colors font-medium text-gray-900"
                            >
                              {campo.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              )}
            </div>
          </div>

          {/* Painel Direito - Editor ou PDF Upload */}
          <div className="flex-1 flex flex-col">
            {tipoDocumento === 'html' ? (
              <>
                {/* Toolbar de Formatação */}
                <div className="bg-gray-100 border-b border-gray-300 p-3 flex flex-wrap gap-2">
                  {/* Formatação de Texto */}
                  <div className="flex gap-1 border-r border-gray-300 pr-2">
                    <button
                      onClick={() => aplicarFormatacao('bold')}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 font-bold text-sm"
                      title="Negrito (Ctrl+B)"
                    >
                      B
                    </button>
                    <button
                      onClick={() => aplicarFormatacao('italic')}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 italic text-sm"
                      title="Itálico (Ctrl+I)"
                    >
                      I
                    </button>
                    <button
                      onClick={() => aplicarFormatacao('underline')}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 underline text-sm"
                      title="Sublinhado (Ctrl+U)"
                    >
                      U
                    </button>
                  </div>

                  {/* Títulos */}
                  <div className="flex gap-1 border-r border-gray-300 pr-2">
                    <button
                      onClick={() => aplicarFormatacao('formatBlock', '<h1>')}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 text-sm font-semibold"
                      title="Título 1"
                    >
                      H1
                    </button>
                    <button
                      onClick={() => aplicarFormatacao('formatBlock', '<h2>')}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 text-sm"
                      title="Título 2"
                    >
                      H2
                    </button>
                    <button
                      onClick={() => aplicarFormatacao('formatBlock', '<p>')}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 text-sm"
                      title="Parágrafo"
                    >
                      P
                    </button>
                  </div>

                  {/* Inserções */}
                  <div className="flex gap-1 border-r border-gray-300 pr-2">
                    <button
                      onClick={inserirImagem}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 text-sm flex items-center gap-1"
                      title="Inserir Imagem do Computador"
                    >
                      🖼️ Imagem
                    </button>
                    <button
                      onClick={inserirLink}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 text-sm flex items-center gap-1"
                      title="Inserir Link"
                    >
                      🔗 Link
                    </button>
                    <button
                      onClick={inserirTabela}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 text-sm flex items-center gap-1"
                      title="Inserir Tabela"
                    >
                      📊 Tabela
                    </button>
                  </div>

                  {/* Elementos Especiais */}
                  <div className="flex gap-1">
                    <button
                      onClick={() => aplicarFormatacao('insertHorizontalRule')}
                      className="px-3 py-1.5 bg-white hover:bg-gray-200 rounded border border-gray-300 text-sm"
                      title="Linha Horizontal"
                    >
                      —
                    </button>
                    <button
                      onClick={inserirLogo}
                      className="px-3 py-1.5 bg-blue-600 text-white hover:bg-blue-700 rounded border border-blue-700 text-sm flex items-center gap-1 font-medium"
                      title="Inserir Logo"
                    >
                      🏢 Logo
                    </button>
                  </div>
                </div>

                {/* Editor Visual */}
                <div className="flex-1 overflow-auto p-6 bg-white">
                  <div
                    ref={editorRef}
                    contentEditable
                    onInput={(e) => setConteudoHtml((e.target as HTMLDivElement).innerHTML)}
                    suppressContentEditableWarning
                    className="min-h-full p-8 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
                    style={{
                      fontFamily: 'Arial, sans-serif',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      color: '#333'
                    }}
                  >
                    {!template && '<p>Digite seu documento aqui...</p>'}
                  </div>
                </div>
              </>
            ) : (
              /* Área de Upload de PDF */
              <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gray-50">
                {pdfUrl ? (
                  <div className="w-full h-full flex flex-col">
                    <div className="flex items-center justify-between mb-4 px-2">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-red-50 rounded-lg">
                          <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 text-sm">{pdfNomeOriginal}</p>
                          <p className="text-xs text-gray-500">PDF importado com sucesso</p>
                        </div>
                      </div>
                      <button
                        onClick={() => { setPdfUrl(''); setPdfNomeOriginal(''); }}
                        className="px-3 py-1.5 text-xs bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors font-medium"
                      >
                        Remover
                      </button>
                    </div>
                    <div className="flex-1 border-2 border-gray-200 rounded-lg overflow-hidden bg-white">
                      <iframe
                        src={pdfUrl}
                        className="w-full h-full min-h-[400px]"
                        title="Preview do PDF"
                      />
                    </div>
                  </div>
                ) : (
                  <div
                    className={`w-full max-w-lg border-2 border-dashed rounded-xl p-12 text-center transition-colors ${
                      uploadingPdf ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
                    }`}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const file = e.dataTransfer.files[0];
                      if (file && file.type === 'application/pdf') {
                        handleUploadPdf(file);
                      } else {
                        alert('Apenas arquivos PDF são permitidos');
                      }
                    }}
                  >
                    {uploadingPdf ? (
                      <>
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-blue-600 font-medium">Fazendo upload do PDF...</p>
                      </>
                    ) : (
                      <>
                        <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <DocumentArrowUpIcon className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          Importar PDF
                        </h3>
                        <p className="text-sm text-gray-500 mb-6">
                          Arraste um arquivo PDF aqui ou clique para selecionar
                        </p>
                        <button
                          onClick={() => {
                            const input = document.createElement('input');
                            input.type = 'file';
                            input.accept = '.pdf';
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement).files?.[0];
                              if (file) handleUploadPdf(file);
                            };
                            input.click();
                          }}
                          className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm"
                        >
                          Selecionar PDF
                        </button>
                        <p className="text-xs text-gray-400 mt-4">
                          O PDF será exibido tal como foi importado, sem campos dinâmicos
                        </p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {tipoDocumento === 'html' ? (
              <>💡 Use <code className="px-2 py-1 bg-gray-200 rounded">{'{{campo.id}}'}</code> para inserir campos dinâmicos</>
            ) : (
              <>📄 O PDF será exibido exatamente como importado</>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              onClick={handleSalvar}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {template ? 'Salvar Alterações' : 'Criar Documento'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditorDocumento;
